/**
 * Sevens (Satte Pe Satta) — server-authoritative rules engine (four-player shedding game).
 *
 * Rules implemented:
 *  - One 52-card deck dealt out, 13 each. The holder of the seven of diamonds starts and must play it.
 *  - Each suit is built outward from its seven: a seven may be played to start its suit (once the game is under way),
 *    and after that only the card one rank above the highest or one below the lowest card already played in that suit.
 *    Ace is low (the run for a suit is A 2 3 … 7 … J Q K).
 *  - A player who can play may not pass; a player with no playable card passes automatically.
 *  - A player who plays their last card is out. The game ends when only one player still holds cards. Players finish
 *    in the order they went out; the player left holding cards finishes last.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface SevensConfig {
  /** Hard cap on turns so a game always terminates. */
  maxTurns: number;
}

export type SevensPhase = 'PLAYING' | 'MATCH_END';

/** The run of a suit already on the table: ranks 1 (ace) to 13 (king); null when the suit has not been started. */
export type SevensRun = { low: number; high: number } | null;

export interface SevensState {
  config: SevensConfig;
  players: string[];
  phase: SevensPhase;
  hands: CardId[][];
  board: Record<Suit, SevensRun>;
  turn: number;
  /** Seats in the order they went out. */
  out: number[];
  /** Times each seat has had to pass. */
  passes: number[];
  turns: number;
  lastPlay: { seat: number; card: CardId } | null;
}

export type SevensAction = { type: 'play'; card: CardId } | { type: 'pass' };

export interface SevensLegal {
  cards: CardId[];
  canPass: boolean;
}

export interface SevensView {
  gameType: 'SEVENS';
  config: SevensConfig;
  seat: number | null;
  phase: SevensPhase;
  hand: CardId[];
  handCounts: number[];
  board: Record<Suit, SevensRun>;
  turn: number;
  out: number[];
  passes: number[];
  lastPlay: { seat: number; card: CardId } | null;
  /** Cards each seat holds (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: SevensConfig = { maxTurns: 800 };
const SEATS = 4;
export const SEVENS_START_CARD = '7D';

/** Rank on the run: ace 1 … king 13. */
export const sevensRank = (card: CardId): number => {
  const r = parseCard(card).rank;
  return r === 14 ? 1 : r;
};

/** Is `card` playable on the board? */
export function sevensPlayable(card: CardId, board: Record<Suit, SevensRun>): boolean {
  const { suit } = parseCard(card);
  const rank = sevensRank(card);
  const run = board[suit];
  if (run === null) return rank === 7;
  return rank === run.low - 1 || rank === run.high + 1;
}

export function sevensLegal(hand: readonly CardId[], board: Record<Suit, SevensRun>, firstMove: boolean): SevensLegal {
  if (firstMove) {
    // The very first card is the seven of diamonds.
    return hand.includes(SEVENS_START_CARD) ? { cards: [SEVENS_START_CARD], canPass: false } : { cards: [], canPass: true };
  }
  const cards = hand.filter((c) => sevensPlayable(c, board));
  return { cards, canPass: cards.length === 0 };
}

export const sevensPoints = (hand: readonly CardId[]): number => hand.reduce((n, c) => n + sevensRank(c), 0);

const emptyBoard = (): Record<Suit, SevensRun> => ({ S: null, H: null, D: null, C: null });

export const sevensEngine: GameEngine<SevensConfig, SevensState, SevensAction, SevensView, SevensLegal> = {
  gameType: 'SEVENS',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxTurns', c.maxTurns, 100, 5000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Sevens needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const deck = shuffle(buildDeck(), rng);
    const hands: CardId[][] = [[], [], [], []];
    deck.forEach((c, i) => hands[i % SEATS]!.push(c));
    const sorted = hands.map((h) => sortHand(h));
    return {
      config,
      players,
      phase: 'PLAYING',
      hands: sorted,
      board: emptyBoard(),
      turn: sorted.findIndex((h) => h.includes(SEVENS_START_CARD)),
      out: [],
      passes: [0, 0, 0, 0],
      turns: 0,
      lastPlay: null,
    };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Sevens action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase === 'PLAYING', 'GAME_OVER', 'The game is over');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;
    const legal = sevensLegal(hand, state.board, state.turns === 0);

    if (action.type === 'pass') {
      assertEngine(legal.canPass, 'ILLEGAL_MOVE', 'You have a card you can play');
      state.passes[seat]!++;
      events.push({ type: 'pass', seat });
    } else {
      assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
      assertEngine(legal.cards.includes(action.card), 'ILLEGAL_MOVE', 'That card cannot be played now');
      removeCards(hand, [action.card]);
      const { suit } = parseCard(action.card);
      const rank = sevensRank(action.card);
      const run = state.board[suit];
      state.board[suit] = run === null ? { low: rank, high: rank } : { low: Math.min(run.low, rank), high: Math.max(run.high, rank) };
      state.lastPlay = { seat, card: action.card };
      events.push({ type: 'cardPlayed', seat, card: action.card });
      if (hand.length === 0) {
        state.out.push(seat);
        events.push({ type: 'out', seat, place: state.out.length });
      }
    }
    state.turns++;

    const left = state.hands.filter((h) => h.length > 0).length;
    if (left <= 1 || state.turns >= state.config.maxTurns) {
      state.phase = 'MATCH_END';
      events.push({ type: 'matchEnd', standings: standings(state) });
      return events;
    }
    // Next seat that still holds cards. A seat with nothing playable passes automatically.
    const following = (from: number) => {
      let n = (from + 1) % SEATS;
      while (state.hands[n]!.length === 0) n = (n + 1) % SEATS;
      return n;
    };
    let next = following(seat);
    for (let tries = 0; tries < SEATS && sevensLegal(state.hands[next]!, state.board, false).cards.length === 0; tries++) {
      state.passes[next]!++;
      state.turns++;
      events.push({ type: 'pass', seat: next, auto: true });
      next = following(next);
    }
    state.turn = next;
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (state.phase !== 'PLAYING' || seat !== state.turn) return { cards: [], canPass: false };
    return sevensLegal(state.hands[seat]!, state.board, state.turns === 0);
  },

  getTimeoutAction(state, seat) {
    const legal = sevensLegal(state.hands[seat]!, state.board, state.turns === 0);
    if (legal.cards.length === 0) return { type: 'pass' };
    // Play the card farthest from the seven: it opens the least for the others.
    const far = [...legal.cards].sort((a, b) => Math.abs(sevensRank(b) - 7) - Math.abs(sevensRank(a) - 7))[0]!;
    return { type: 'play', card: far };
  },

  getView(state, seat) {
    return {
      gameType: 'SEVENS',
      config: state.config,
      seat,
      phase: state.phase,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      board: { S: state.board.S && { ...state.board.S }, H: state.board.H && { ...state.board.H }, D: state.board.D && { ...state.board.D }, C: state.board.C && { ...state.board.C } },
      turn: state.turn,
      out: [...state.out],
      passes: [...state.passes],
      lastPlay: state.lastPlay ? { ...state.lastPlay } : null,
      totals: state.hands.map((h) => h.length),
    };
  },

  getSystemStep: () => null,
  runSystemStep: () => [],

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standings(state);
  },

  getRoundsCompleted: (state) => state.turns,
};

/** Places: players in the order they went out, then whoever is left by fewest points (the loser last). */
function standings(state: SevensState): Standing[] {
  const remaining = [0, 1, 2, 3]
    .filter((s) => !state.out.includes(s))
    .sort((a, b) => sevensPoints(state.hands[a]!) - sevensPoints(state.hands[b]!) || state.hands[a]!.length - state.hands[b]!.length);
  return [...state.out, ...remaining].map((seat, i) => ({ seat, place: i + 1, score: 100 - i * 10 - state.hands[seat]!.length }));
}
